// Reserves room at the bottom of the layout for the Yieldlove anchor ad, which the vendor script
// injects by itself (verified on production 2026-10-07: `div#div-gpt-ad-yieldlove-sticky-<n>`,
// position: fixed, a child of <body>, ~0.6 s after load; 970x90 on desktop, 320x100 on phones).
// Google's policy: an ad must never cover the game, so the grid's last row grows to the anchor's
// real height ("detect, then grow"), counting only the part really on the screen (anchorRoom). The measured height is remembered per browser (owner decision
// 2026-10-07): the next load, and every win reload, reserves it at once so the board does not jump;
// when no anchor shows up within NO_ANCHOR_MS the band is released and forgotten. Nothing is ever
// reserved for a browser where no anchor appeared (ad blocker, declined consent, ads off).
import { useEffect } from 'react';

const VAR = '--ad-anchor-h';
const MEMORY_KEY = 'solitaire_storage:ads:anchor-h';
const NO_ANCHOR_MS = 8000;

function readMemory(): number {
  try {
    const n = Number(localStorage.getItem(MEMORY_KEY));
    return Number.isFinite(n) && n > 0 && n <= 200 ? Math.ceil(n) : 0;
  } catch {
    return 0;
  }
}

function writeMemory(h: number) {
  try {
    if (h > 0) localStorage.setItem(MEMORY_KEY, String(h));
    else localStorage.removeItem(MEMORY_KEY);
  } catch {
    /* no storage */
  }
}

function isAnchor(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (/^div-gpt-ad-yieldlove-sticky/.test(el.id)) return true;
  if (el.id === 'table_bg') return false; // our own picture of the table, as big as the screen (app/app.css)
  if (getComputedStyle(el).position !== 'fixed') return false;
  const r = el.getBoundingClientRect();
  return r.height >= 20 && r.width >= innerWidth * 0.5 && Math.abs(r.bottom - innerHeight) <= 4;
}

/** The room the anchor really takes at the bottom of the screen: from its top edge to the bottom, 0 when
 *  it is hidden (display, visibility, opacity) or moved off the screen (some anchors close or refresh
 *  by sliding down and stay in the page: the band then stayed empty, owner 2026-10-18). */
function anchorRoom(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return 0;
  const r = el.getBoundingClientRect();
  const top = Math.max(r.top, 0);
  const bottom = Math.min(r.bottom, innerHeight);
  return bottom - top >= 20 ? Math.ceil(innerHeight - top) : 0;
}

/** Room the Privacy tab needs in the bottom-left corner (app.css, a 30 px tab) plus a little air. */
const CORNER_W = 40;

/** Is the bottom-left corner beside the ad free? The anchor's box spans the screen, the ad itself is often a
 *  centred 728 / 970 px creative (owner 2026-10-23, iPad: the Privacy tab sat above the band, not in the
 *  corner). Its iframes tell where the ad really starts. */
function cornerFree(el: HTMLElement): boolean {
  const frames = [...el.querySelectorAll('iframe')].map((f) => f.getBoundingClientRect()).filter((r) => r.width > 50 && r.height > 20);
  const left = frames.length ? Math.min(...frames.map((r) => r.left)) : el.getBoundingClientRect().left;
  return left >= CORNER_W;
}

export function AdAnchorBand() {
  useEffect(() => {
    const root = document.documentElement;
    let anchor: HTMLElement | null = null;
    let seen = false;
    // Reserve the remembered height right away; released below if no anchor appears.
    const remembered = readMemory();
    root.style.setProperty(VAR, `${remembered}px`);
    const measure = () => {
      if (anchor && !anchor.isConnected) anchor = null;
      const h = anchor ? anchorRoom(anchor) : 0;
      const shown = h > 0;
      if (shown) {
        seen = true;
        writeMemory(h);
      }
      if (!shown && !seen && remembered) return; // keep the remembered band until the anchor shows or the wait ends
      root.style.setProperty(VAR, `${shown ? h : 0}px`);
      // the Privacy tab goes down into the corner when the ad leaves it free (app.css data-ad-corner)
      if (shown && anchor && cornerFree(anchor)) root.dataset.adCorner = 'free';
      else delete root.dataset.adCorner;
    };
    const giveUp = window.setTimeout(() => {
      if (!seen) {
        writeMemory(0);
        root.style.setProperty(VAR, '0px');
      }
    }, NO_ANCHOR_MS);
    const scan = () => {
      if (anchor?.isConnected) return measure();
      anchor = null;
      for (const el of document.body.children) {
        // our own windows and their dimming layers are never the anchor (the full-screen veil of a
        // non-modal window was taken for it and every window shrank to nothing, 2026-10-17)
        if (el.id === 'root' || el.classList.contains('lbx_window') || el.classList.contains('lbx_overlay') || el.classList.contains('lbx_veil') || el.classList.contains('win_confetti')) continue;
        if (isAnchor(el)) {
          anchor = el;
          break;
        }
      }
      measure();
    };
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: false, attributes: true, attributeFilter: ['style', 'class'] });
    const ro = new ResizeObserver(measure);
    const onResize = () => scan();
    window.addEventListener('resize', onResize);
    // The anchor's own size can change (refresh, close button): watch it once found.
    const watch = window.setInterval(() => {
      if (anchor) {
        ro.observe(anchor);
        measure();
      }
    }, 1000);
    scan();
    return () => {
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      clearInterval(watch);
      clearTimeout(giveUp);
      root.style.removeProperty(VAR);
      delete root.dataset.adCorner;
    };
  }, []);
  return null;
}
